import { Inject, Injectable } from '@angular/core';
import { Router } from '@angular/router';
import { Observable } from 'rxjs';
import { catchError, tap } from 'rxjs/operators';

import { AuthServiceLogin } from '®shared/auth/auth-service-login';
import { AuthorizationFailedException } from '®shared/auth/authorization-failed-exception';
import { PersonaUser } from '®shared/auth/persona-user';
import { AppConfig } from '®shared/config';

import { AnonymousAuthService } from './anonymous-auth.service';
import { AuthProvider } from './auth-provider';
import { AUTHENTICATED_AUTH_SERVICE } from './authenticated-auth.service.provider';
import { AuthenticationFailedException } from './authentication-failed-exception';
import { User } from './user';
import { UserScopesService } from './user-scopes.service';

@Injectable()
export class AuthService
  extends AuthProvider<User>
  implements AuthServiceLogin
{
  private isAnonymousAllowed: boolean;
  private delegatedAuthProvider: AuthProvider<User>;
  private authenticatedAuthService: AuthProvider<User> & AuthServiceLogin;

  constructor(
    private anonymousAuthService: AnonymousAuthService,
    appConfig: AppConfig,
    @Inject(AUTHENTICATED_AUTH_SERVICE)
    authenticatedAuthService: AuthProvider<User> & AuthServiceLogin,
    router: Router,
    userScopesService: UserScopesService,
  ) {
    super(appConfig, router, userScopesService);
    this.authenticatedAuthService = authenticatedAuthService;

    this.isAnonymousAllowed = appConfig.tenant.anonymousAccessToken
      ? true
      : false;
    if (!this.isAnonymousAllowed) {
      this.delegatedAuthProvider = this.authenticatedAuthService;
    }
  }

  /**
   * Get the current authentication token.
   *
   * @return {Observable<User>} The user, once the current token has been retrieved
   */
  getCurrentToken(): Observable<User> {
    // use the already-set delegate
    if (this.delegatedAuthProvider) {
      return this.delegatedAuthProvider.getCurrentToken();
    }

    // if anonymous is not permitted, we must use Persona
    if (!this.isAnonymousAllowed || this.isInExplicitLoginMode()) {
      this.delegatedAuthProvider = this.authenticatedAuthService;
      return this.authenticatedAuthService.getCurrentToken();
    }

    // use Persona...
    return this.authenticatedAuthService.getCurrentToken().pipe(
      tap(() => {
        this.delegatedAuthProvider = this.authenticatedAuthService;
      }),
      catchError(error => {
        if (!(error instanceof AuthenticationFailedException)) {
          throw error;
        }

        // ... but fall back to anonymous authentication
        return this.anonymousAuthService.getCurrentToken().pipe(
          tap(() => {
            this.delegatedAuthProvider = this.anonymousAuthService;
          }),
        );
      }),
    );
  }

  /**
   * Refresh the current authentication token.
   *
   * @return {Observable<User>} The user, once the token has been refreshed
   */
  refreshToken(): Observable<User> {
    const provider = this.delegatedAuthProvider
      ? this.delegatedAuthProvider
      : this.authenticatedAuthService;
    return provider.refreshToken();
  }

  /**
   * Obtain a new token for the user
   *
   * @param  {?string}          returnUrl an optional return URL, for redirection
   * @return {Observable<User>}           The user, once a new token is obtained
   */
  obtainNewToken(returnUrl?: string): Observable<User> {
    const provider = this.delegatedAuthProvider
      ? this.delegatedAuthProvider
      : this.authenticatedAuthService;
    return provider.obtainNewToken(returnUrl);
  }

  /**
   * The query parameters `login` or `talis_login` indicate that the user has explicitly
   * indicated that they wish to log in.
   *
   * @return {boolean} `true` when `login` or `talis_login` is specified; `false` otherwise
   */
  public isInExplicitLoginMode(): boolean {
    const uri = this.getCurrentUri();
    const query = uri.query(true);
    return query['login'] || query['talis_login'] ? true : false;
  }

  /**
   * Trigger the login flow. This redirects to Persona for login, and returns
   * to the *specified* (relative) URL.
   * Use this during route navigation (e.g. resolvers and guards) when access
   * to the router state is available.
   */
  public loginDuringRouteNavigation(stateUrl: string): void {
    this.authenticatedAuthService.loginDuringRouteNavigation(stateUrl);
  }

  /**
   * Trigger the login flow. This redirects to Persona for login, and returns
   * to the *current* (absolute) URL.
   * DO NOT USE during route navigation (e.g. resolvers and guards), as the
   * current URL is not stable until routing completes.
   * See `loginDuringRouteNavigation` for an alternative during routing.
   */
  public login(): void {
    this.authenticatedAuthService.login();
  }

  /**
   * Update the current user for this authentication service and the underlying
   * authentication service it is currently using (persona or persona PKCE)
   * @param user The logged in user
   */
  public updateUser(user: User): void {
    super.updateUser(user);
    this.authenticatedAuthService.updateUser(user);
  }

  /**
   * Authorize a Persona user based on the required scopes for a user to be considered
   * authorized to access a page.
   */
  public authorizePersonaUser(
    personaUser: PersonaUser,
    isTalisLogin: boolean,
  ): PersonaUser {
    const requiredScopes =
      this.userScopesService.requiredScopesToAuthorize(isTalisLogin);
    const hasRequiredScopes =
      this.userScopesService.hasSomeScopeOrHigherPrivileges(
        personaUser,
        ...requiredScopes,
      );

    if (!hasRequiredScopes) {
      throw new AuthorizationFailedException(
        'You do not have the required permissions to access this page.',
      );
    }

    return personaUser;
  }
}
